import * as React from 'react';
import { SerializedStyles } from '@emotion/react';
import clsx from 'clsx';

import LoadingComponent from '@tock/shared/components/LoadingComponent';
import ScrollLockComponent from '@tock/shared/components/ScrollLockComponent';

/**
 * Modern layout component for Clowncar pages with header, footer, and scrollable content area.
 *
 * This V2 version is a drop-in replacement for ClowncarBody using function components.
 * Uses the same CSS classes for scroll behavior (ScrollLock, ClowncarBody-scrollContext).
 *
 * @example
 * <ClowncarBodyV2
 *   header={<PageHeader />}
 *   footer={<PageFooter />}
 *   isLoading={loading}
 *   spinnerText="Loading data..."
 * >
 *   {content}
 * </ClowncarBodyV2>
 */

interface ClowncarBodyV2Props {
  children?: React.ReactNode;
  contentClassName?: string;
  contentCss?: SerializedStyles;
  footer?: React.ReactNode;
  header?: React.ReactNode;
  isLoading?: boolean;
  showOverlay?: boolean;
  scrollContextClassName?: string;
  spinnerText?: string;
}

const ClowncarBodyV2: React.FC<ClowncarBodyV2Props> = ({
  children,
  contentClassName: contentClassNameFromProps,
  contentCss,
  footer,
  header,
  showOverlay = false,
  isLoading = false,
  scrollContextClassName: scrollContextClassNameFromProps,
  spinnerText,
}) => {
  const contentClassName = clsx('ClowncarBody-content', contentClassNameFromProps);
  const scrollContextClassName = clsx(
    'ClowncarBody-scrollContext',
    scrollContextClassNameFromProps,
    {
      'has-footer': footer,
      'has-header': header,
    }
  );

  return (
    <div className="ClowncarBody">
      {header && <header className="ClowncarBody-header">{header}</header>}

      <ScrollLockComponent id="ClowncarBodyContent" className={scrollContextClassName}>
        <LoadingComponent
          css={contentCss}
          containerClassName={contentClassName}
          isLoading={isLoading}
          showOverlay={showOverlay}
          spinnerClassName="ClowncarBody-spinner"
          spinnerText={spinnerText}
        >
          {children}
        </LoadingComponent>
      </ScrollLockComponent>

      {footer && <footer className="ClowncarBody-footer">{footer}</footer>}
    </div>
  );
};

export default ClowncarBodyV2;
